import React, { useState, useEffect, useCallback } from 'react';
import AdminLayout from '@/components/layout/AdminLayout';
import { API } from '@/App';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from '@/components/ui/dialog';
import {
  CheckCircle2, Circle, ArrowRight, Layers, Calendar, Pill, Leaf,
  FileText, Play, RefreshCw, ChevronRight, Shield, Rocket,
} from 'lucide-react';
import { toast } from 'sonner';

const STEP_ICONS = {
  protocol_wizard: Layers,
  executable_review: FileText,
  schedule_editor: Calendar,
  lifeflo_assignment: Leaf,
  medication_assignment: Pill,
  publish_phase: Play,
};

const STEP_COLORS = {
  protocol_wizard: '#C89B7B',
  executable_review: '#7BA3C8',
  schedule_editor: '#4E7A6B',
  lifeflo_assignment: '#10b981',
  medication_assignment: '#f59e0b',
  publish_phase: '#8b5cf6',
};

export default function ProtocolFlowPage() {
  const navigate = useNavigate();
  const [subscribers, setSubscribers] = useState([]);
  const [selectedSub, setSelectedSub] = useState('');
  const [flowStatus, setFlowStatus] = useState(null);
  const [golive, setGolive] = useState(null);
  const [loading, setLoading] = useState(false);
  const [showPublish, setShowPublish] = useState(false);
  const [showGoLive, setShowGoLive] = useState(false);

  useEffect(() => {
    axios.get(`${API}/users`).then(r => {
      const users = Array.isArray(r.data) ? r.data : r.data?.users || [];
      setSubscribers(users.filter(u => u.role === 'subscriber' && (u.status === 'onboarding_completed' || u.status === 'active' || u.is_active)));
    }).catch(() => {});
  }, []);

  const loadFlow = useCallback(async (subId) => {
    if (!subId) return;
    setLoading(true);
    try {
      const [flow, gl] = await Promise.all([
        axios.get(`${API}/protocol-flow/status/${subId}`),
        axios.get(`${API}/protocol-flow/go-live/${subId}`),
      ]);
      setFlowStatus(flow.data);
      setGolive(gl.data);
    } catch (e) { toast.error('Failed to load'); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { if (selectedSub) loadFlow(selectedSub); }, [selectedSub, loadFlow]);

  const handleStepClick = (step) => {
    if (step.path) {
      navigate(step.path + (selectedSub ? `?subscriber=${selectedSub}` : ''));
    }
  };

  const handleCompleteStep = async (stepKey) => {
    try {
      await axios.post(`${API}/protocol-flow/status/${selectedSub}/complete-step`, { step_key: stepKey });
      toast.success('Step marked complete');
      loadFlow(selectedSub);
    } catch (e) { toast.error('Failed'); }
  };

  const handlePublish = async () => {
    try {
      await axios.post(`${API}/protocol-flow/status/${selectedSub}/publish`, {});
      toast.success('Active phase published');
      setShowPublish(false);
      loadFlow(selectedSub);
    } catch (e) { toast.error('Failed'); }
  };

  const handleGoLiveCheck = async (key, checked) => {
    try {
      await axios.post(`${API}/protocol-flow/go-live/${selectedSub}/check`, { key, checked });
      loadFlow(selectedSub);
    } catch (e) { toast.error('Failed'); }
  };

  const handleStartLive = async () => {
    try {
      await axios.post(`${API}/protocol-flow/go-live/${selectedSub}/start-live`, {});
      toast.success('Subscriber is now LIVE');
      setShowGoLive(false);
      loadFlow(selectedSub);
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
  };

  const allStepsDone = flowStatus?.all_required_complete;

  return (
    <AdminLayout>
      <div data-testid="protocol-flow-page" className="p-6 max-w-5xl mx-auto space-y-6">
        <div>
          <h1 className="text-2xl font-bold text-white tracking-tight">Protocol Flow</h1>
          <p className="text-sm text-white/40 mt-0.5">Guided protocol build: Wizard → Review → Schedule → Assign → Publish → Go-Live</p>
        </div>

        {/* Subscriber Selector */}
        <Card className="bg-[#0d1117] border-white/5">
          <CardContent className="p-4">
            <p className="text-[10px] text-white/30 uppercase tracking-wider mb-2">Select Subscriber</p>
            <Select value={selectedSub} onValueChange={setSelectedSub}>
              <SelectTrigger data-testid="protocol-sub-select" className="bg-white/[0.03] border-white/[0.06] text-white">
                <SelectValue placeholder="Choose subscriber..." />
              </SelectTrigger>
              <SelectContent className="bg-[#0d1117] border-white/10 max-h-[300px]">
                {subscribers.map(s => (
                  <SelectItem key={s.id} value={s.id} className="text-white text-xs">{s.name} ({s.email})</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </CardContent>
        </Card>

        {loading && <div className="text-center py-8"><RefreshCw className="w-5 h-5 text-white/20 animate-spin mx-auto" /></div>}

        {flowStatus && !loading && (
          <>
            {/* Protocol Steps */}
            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <p className="text-[10px] text-white/30 uppercase tracking-wider">Protocol Build Steps</p>
                <Badge className={`text-[9px] ${allStepsDone ? 'bg-emerald-800/30 text-emerald-300' : 'bg-white/5 text-white/40'}`}>
                  {flowStatus.completed_count}/{flowStatus.total_steps} complete
                </Badge>
              </div>

              {flowStatus.steps.map((step, i) => {
                const Icon = STEP_ICONS[step.key] || Layers;
                const color = STEP_COLORS[step.key] || '#64748b';
                const isLast = i === flowStatus.steps.length - 1;

                return (
                  <div key={step.key} className="flex items-stretch gap-3">
                    {/* Timeline */}
                    <div className="flex flex-col items-center w-8">
                      <div className={`w-8 h-8 rounded-full flex items-center justify-center border-2 ${step.completed ? 'border-emerald-500 bg-emerald-500/10' : 'border-white/10 bg-white/[0.02]'}`}>
                        {step.completed
                          ? <CheckCircle2 className="w-4 h-4 text-emerald-400" />
                          : <Icon className="w-3.5 h-3.5" style={{ color }} />}
                      </div>
                      {!isLast && <div className={`w-0.5 flex-1 my-1 ${step.completed ? 'bg-emerald-500/30' : 'bg-white/[0.06]'}`} />}
                    </div>

                    {/* Card */}
                    <Card data-testid={`protocol-step-${step.key}`}
                      className={`flex-1 mb-1 cursor-pointer transition-all ${step.completed ? 'bg-emerald-900/5 border-emerald-800/15' : 'bg-white/[0.02] border-white/[0.06]'} hover:border-white/15`}
                      onClick={() => step.key === 'publish_phase' ? setShowPublish(true) : handleStepClick(step)}>
                      <CardContent className="p-3 flex items-center justify-between">
                        <div>
                          <div className="flex items-center gap-2">
                            <p className="text-sm text-white/80">{step.label}</p>
                            {!step.required && <Badge className="bg-white/5 text-white/25 text-[7px]">optional</Badge>}
                          </div>
                        </div>
                        <div className="flex items-center gap-2">
                          {!step.completed && step.key !== 'publish_phase' && (
                            <Button variant="ghost" size="sm" className="h-6 px-2 text-[9px] text-white/30"
                              onClick={(e) => { e.stopPropagation(); handleCompleteStep(step.key); }}>
                              Mark Done
                            </Button>
                          )}
                          {step.path && <ChevronRight className="w-3.5 h-3.5 text-white/20" />}
                        </div>
                      </CardContent>
                    </Card>
                  </div>
                );
              })}
            </div>

            {/* Go-Live Readiness */}
            {golive && (
              <Card className="bg-[#0d1117] border-white/5">
                <CardContent className="p-4">
                  <div className="flex items-center justify-between mb-3">
                    <div className="flex items-center gap-2">
                      <Rocket className="w-4 h-4 text-violet-400" />
                      <p className="text-sm font-medium text-white">Go-Live Readiness</p>
                    </div>
                    <Badge className={`text-[9px] ${golive.ready_for_live ? 'bg-emerald-800/30 text-emerald-300' : 'bg-amber-800/30 text-amber-300'}`}>
                      {golive.mandatory_progress}
                    </Badge>
                  </div>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-1.5 mb-3">
                    {golive.mandatory.map(item => (
                      <div key={item.key} className="flex items-center gap-2 p-2 rounded bg-white/[0.02]"
                        onClick={() => !item.checked && handleGoLiveCheck(item.key, true)}>
                        {item.checked
                          ? <CheckCircle2 className="w-3.5 h-3.5 text-emerald-400 flex-shrink-0" />
                          : <Circle className="w-3.5 h-3.5 text-white/20 flex-shrink-0 cursor-pointer" />}
                        <span className={`text-xs ${item.checked ? 'text-white/60' : 'text-white/40'}`}>{item.label}</span>
                      </div>
                    ))}
                  </div>
                  <details className="text-xs text-white/30">
                    <summary className="cursor-pointer hover:text-white/50 mb-1.5">Optional items</summary>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-1.5">
                      {golive.optional.map(item => (
                        <div key={item.key} className="flex items-center gap-2 p-2 rounded bg-white/[0.01]"
                          onClick={() => handleGoLiveCheck(item.key, !item.checked)}>
                          {item.checked
                            ? <CheckCircle2 className="w-3.5 h-3.5 text-emerald-400/50 flex-shrink-0" />
                            : <Circle className="w-3.5 h-3.5 text-white/15 flex-shrink-0 cursor-pointer" />}
                          <span className={`text-[11px] ${item.checked ? 'text-white/50' : 'text-white/30'}`}>{item.label}</span>
                        </div>
                      ))}
                    </div>
                  </details>
                  {golive.ready_for_live && !golive.live_started && (
                    <Button data-testid="start-live-btn" className="w-full mt-3 bg-emerald-600 hover:bg-emerald-700 text-white"
                      onClick={() => setShowGoLive(true)}>
                      <Rocket className="w-4 h-4 mr-2" /> Start Live
                    </Button>
                  )}
                  {golive.live_started && (
                    <div className="flex items-center gap-2 mt-3 p-2 bg-emerald-900/10 rounded-lg border border-emerald-800/20">
                      <CheckCircle2 className="w-4 h-4 text-emerald-400" />
                      <span className="text-xs text-emerald-400">Subscriber is LIVE</span>
                    </div>
                  )}
                </CardContent>
              </Card>
            )}
          </>
        )}

        {/* Publish Dialog */}
        <Dialog open={showPublish} onOpenChange={setShowPublish}>
          <DialogContent className="bg-[#0d1117] border-white/10 text-white">
            <DialogHeader><DialogTitle>Publish Active Phase</DialogTitle></DialogHeader>
            <p className="text-sm text-white/60 py-4">This marks the protocol as ready for {flowStatus?.subscriber_name}. The subscriber's active phase will be available for execution.</p>
            <DialogFooter>
              <Button variant="ghost" onClick={() => setShowPublish(false)} className="text-white/60">Cancel</Button>
              <Button className="bg-violet-600 hover:bg-violet-700" onClick={handlePublish}>Publish</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Start Live Dialog */}
        <Dialog open={showGoLive} onOpenChange={setShowGoLive}>
          <DialogContent className="bg-[#0d1117] border-white/10 text-white">
            <DialogHeader><DialogTitle>Start Live</DialogTitle></DialogHeader>
            <p className="text-sm text-white/60 py-4">This will activate the subscriber's live CARE OS experience. Daily execution, monitoring, and all CARE flows will begin.</p>
            <DialogFooter>
              <Button variant="ghost" onClick={() => setShowGoLive(false)} className="text-white/60">Cancel</Button>
              <Button className="bg-emerald-600 hover:bg-emerald-700" onClick={handleStartLive}>
                <Rocket className="w-4 h-4 mr-2" /> Confirm Live Start
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </AdminLayout>
  );
}
